"use client";
import { BliMedlemLoader } from "@/app/bli-medlem/bli-medlem-loaders";
import { MultiStepFormContext } from "@/components/bli-medlem/form-state-context";
import { UserContext } from "@/contexts/user-context";
import {
  type BlockUnionFragment,
  type CenterFragment,
  type CustomerTypeFragment,
  type FullUserFragment,
  type OrganizationFragment,
  type SubscriptionFragment,
  useGetInitialResourcesSuspenseQuery,
  useGetOrderSuspenseQuery,
  useGetSubscriptionsSuspenseQuery,
} from "@/generated/client.generated";
import { Logger } from "@/utils/logger";
import { genderToString } from "@/utils/mapping";
import { ROOT_URL, paths } from "@/utils/paths-utils";
import storageUtils from "@/utils/storage-utils";
import { type ApolloError, skipToken } from "@apollo/client";
import dayjs from "dayjs";
import { usePathname, useRouter } from "next/navigation";
import { parseAsString, useQueryState } from "nuqs";
import { useContext } from "react";
import { BedriftSteps, BliMedlemSteps } from "./bli-medlem-steps";
import { Form } from "./form";
import {
  EMPTY_FORM_DATA,
  type SelectedFormValues,
  type SessionContactInfo,
  SessionStorage,
  UserJourney,
} from "./types";
import { userJourneyToRecurrenceString } from "./utils";

interface FormWrapperProps {
  userJourney: UserJourney;
  blocks?: ReadonlyArray<BlockUnionFragment>;
}

export const FormWrapper = ({ userJourney, blocks }: FormWrapperProps) => {
  const pathName = usePathname();
  const router = useRouter();

  const { user, token } = useContext(UserContext);
  const redirectUrl = `${ROOT_URL}${pathName}`;

  const [mainCenterId] = useQueryState("mainCenterId", parseAsString.withDefault("0"));
  const [customerTypeId] = useQueryState("customerTypeId", parseAsString.withDefault("0"));
  const [accountTypeId] = useQueryState("accountTypeId", parseAsString.withDefault("0"));
  const [organization] = useQueryState("organization", parseAsString.withDefault(""));
  const [discountCode] = useQueryState("discountCode", parseAsString);
  const [email] = useQueryState("email", parseAsString);
  const [orderId] = useQueryState("orderid", parseAsString);
  const [receiptId] = useQueryState("receiptid", parseAsString);
  const [errorCode] = useQueryState("error", parseAsString);

  const { getItem } = storageUtils();
  const sessionContactInfo = getItem<SessionContactInfo>(SessionStorage.ContactInfo, "SESSION");

  const { data: getOrderData, error: getOrderError } = useGetOrderSuspenseQuery(
    orderId
      ? {
          variables: { id: orderId, redirectUrl: redirectUrl },
          fetchPolicy: "network-only",
        }
      : skipToken,
  );
  const { data: initialResources } = useGetInitialResourcesSuspenseQuery();

  const CustomerTypes = initialResources?.CustomerTypes;
  const Centers = initialResources?.Centers;
  const Subscriptions = initialResources?.Subscriptions;
  const Organizations = initialResources?.Organizations;

  if (orderId && getOrderData?.Order?.status?.name === "Debiterad") {
    router.push(paths.bliMedlemKvittering(orderId));
  }
  const organizationMatch = Organizations?.find((o) => o.id === organization);

  const { data: getSubscriptionsData } = useGetSubscriptionsSuspenseQuery(
    organizationMatch
      ? {
          variables: {
            centerId: mainCenterId,
            code: discountCode ?? "",
            customerTypeId: customerTypeId,
            recurrence: userJourneyToRecurrenceString(userJourney),
            organization: organizationMatch.id,
          },
        }
      : skipToken,
  );

  const customerTypeMatch = CustomerTypes?.find((ct) => ct.id === customerTypeId);
  const centerMatch = Centers?.find((c) => c.id === mainCenterId);
  const subscriptionMatch =
    getSubscriptionsData?.Subscriptions.find((ds) => ds.id === accountTypeId) ??
    Subscriptions?.find((s) => s.id === accountTypeId);

  const userJourneyIsBedrift = userJourney === UserJourney.Organization;
  const state = userJourneyIsBedrift
    ? getInitialStateFromParamsOrganization(
        customerTypeMatch,
        centerMatch,
        subscriptionMatch,
        discountCode,
        email,
        orderId,
        getOrderError,
        errorCode,
        user,
        token,
        sessionContactInfo,
        organizationMatch,
      )
    : getInitialStateFromParams(
        customerTypeMatch,
        centerMatch,
        subscriptionMatch,
        discountCode,
        email,
        userJourney,
        orderId,
        getOrderError,
        errorCode,
        user,
        token,
        sessionContactInfo,
      );

  if (!state || discountCode || receiptId) {
    return <BliMedlemLoader />;
  }

  return (
    <MultiStepFormContext blocks={blocks} steps={userJourneyIsBedrift ? BedriftSteps : BliMedlemSteps} data={state}>
      <Form />
    </MultiStepFormContext>
  );
};

function getInitialStateFromParamsOrganization(
  customerTypeMatch: CustomerTypeFragment | undefined,
  centerMatch: CenterFragment | undefined,
  subscriptionMatch: SubscriptionFragment | undefined,
  discountCode: string | null,
  email: string | null,
  orderId: string | null,
  getOrderError: ApolloError | undefined,
  errorCode: string | null,
  user: FullUserFragment | undefined,
  token: string | undefined,
  sessionContactInfo: SessionContactInfo | null,
  organizationMatch: OrganizationFragment | undefined,
) {
  const selectedValues: SelectedFormValues = [];
  if (customerTypeMatch) {
    selectedValues.push({
      name: BedriftSteps[0].title,
      shortName: BedriftSteps[0].shortTitle,
      stepIndex: 0,
      value: customerTypeMatch.title,
    });
  }

  if (organizationMatch) {
    selectedValues.push({
      name: BedriftSteps[1].title,
      shortName: BedriftSteps[1].shortTitle,
      stepIndex: 1,
      value: organizationMatch.name,
    });
  }

  if (organizationMatch && centerMatch && subscriptionMatch) {
    selectedValues.push({
      name: BedriftSteps[2].title,
      shortName: BedriftSteps[2].shortTitle,
      stepIndex: 2,
      value: subscriptionMatch.name,
    });
  }

  if (email) {
    selectedValues.push({
      name: BedriftSteps[3].title,
      shortName: BedriftSteps[3].shortTitle,
      stepIndex: 3,
      value: email,
    });
  }

  const currentFormData = {
    accountType: subscriptionMatch,
    customerType: customerTypeMatch,
    discountCode: discountCode,
    mainCenter: centerMatch,
    selectedValues: selectedValues,
    organization: organizationMatch,
  };

  const state = {
    ...EMPTY_FORM_DATA,
    ...currentFormData,
    userJourney: UserJourney.Organization,
  };

  //add user data if has sessionContactInfo otherwise if the user is logged in
  if (sessionContactInfo) {
    state.contactInfo = sessionContactInfo.contactInfo;
  } else if (user) {
    state.contactInfo = {
      ...EMPTY_FORM_DATA.contactInfo,
      address: user.streetAddress ?? "",
      birthdate: dayjs(user.birthDate).format("YYYY-MM-DD") ?? "",
      email: user.email || "",
      firstName: user.firstName || "",
      gender: user.gender ? genderToString(user.gender) : "",
      lastName: user.lastName,
      phone: user.phone ?? "",
      postalAddress: user.postalName ?? "",
      postalCode: user?.postalCode ?? "",
    };
  }

  //always set userId and token if we have it
  if (user) {
    state.user = {
      accessToken: token,
      userId: user.id,
    };
  }
  // set first step based on data
  state.currentStepIndex = 0;

  if (state.customerType) {
    state.currentStepIndex = 1;
  }
  if (state.customerType && state.mainCenter && state.organization) {
    state.currentStepIndex = 2;
  }
  if (state.customerType && state.mainCenter && state.accountType && state.organization) {
    state.currentStepIndex = 3;
  }

  if (orderId && (getOrderError || errorCode)) {
    Logger.error(getOrderError ?? errorCode);
    state.currentStepIndex = 4;
  } else if (state.customerType && state.mainCenter && state.accountType && state.organization && email) {
    state.currentStepIndex = 4;
  }

  return state;
}

function getInitialStateFromParams(
  customerTypeMatch: CustomerTypeFragment | undefined,
  centerMatch: CenterFragment | undefined,
  subscriptionMatch: SubscriptionFragment | undefined,
  discountCode: string | null,
  email: string | null,
  userJourney: UserJourney,
  orderId: string | null,
  getOrderError: ApolloError | undefined,
  errorCode: string | null,
  user: FullUserFragment | undefined,
  token: string | undefined,
  sessionContactInfo: SessionContactInfo | null,
) {
  const selectedValues: SelectedFormValues = [];
  if (customerTypeMatch) {
    selectedValues.push({
      name: BliMedlemSteps[0].title,
      shortName: BliMedlemSteps[0].shortTitle,
      stepIndex: 0,
      value: customerTypeMatch.title,
    });
  }
  if (centerMatch) {
    selectedValues.push({
      name: BliMedlemSteps[1].title,
      shortName: BliMedlemSteps[1].shortTitle,
      stepIndex: 1,
      value: centerMatch.page.title,
    });
  }

  if (subscriptionMatch) {
    selectedValues.push({
      name: BliMedlemSteps[2].title,
      shortName: BliMedlemSteps[2].shortTitle,
      stepIndex: 2,
      value: subscriptionMatch.name,
    });
  }

  //add user data if user is logged in
  if (email) {
    selectedValues.push({
      name: BliMedlemSteps[3].title,
      shortName: BliMedlemSteps[3].shortTitle,
      stepIndex: 3,
      value: email,
    });
  }
  const currentFormData = {
    accountType: subscriptionMatch,
    customerType: customerTypeMatch,
    discountCode: discountCode,
    mainCenter: centerMatch,
    selectedValues: selectedValues,
  };

  const state = {
    ...EMPTY_FORM_DATA,
    ...currentFormData,
    userJourney: userJourney,
  };

  //add user data if has sessionContactInfo otherwise if the user is logged in
  if (sessionContactInfo) {
    state.contactInfo = sessionContactInfo.contactInfo;
    state.onBehalfOf = sessionContactInfo.onBehalfOf;
  } else if (user) {
    state.contactInfo = {
      ...EMPTY_FORM_DATA.contactInfo,
      address: user.streetAddress ?? "",
      birthdate: dayjs(user.birthDate).format("YYYY-MM-DD") ?? "",
      email: user.email || "",
      firstName: user.firstName || "",
      gender: user.gender ? genderToString(user.gender) : "",
      lastName: user.lastName,
      phone: user.phone ?? "",
      postalAddress: user.postalName ?? "",
      postalCode: user?.postalCode ?? "",
    };
  }

  //always set userId and token if we have it
  if (user) {
    state.user = {
      accessToken: token,
      userId: user.id,
    };
  }
  // set first step based on data
  state.currentStepIndex = 0;

  if (state.customerType) {
    state.currentStepIndex = 1;
  }
  if (state.customerType && state.mainCenter) {
    state.currentStepIndex = 2;
  }
  if (state.customerType && state.mainCenter && state.accountType) {
    state.currentStepIndex = 3;
  }

  if (state.customerType && state.mainCenter && state.accountType && email) {
    state.currentStepIndex = 4;
  }
  if (orderId && (getOrderError || errorCode)) {
    Logger.error(getOrderError ?? errorCode);
    state.currentStepIndex = 4;
  }

  return state;
}
